// src/utils/subscription.js

const STORAGE_KEY = "tt_subscription";           // legacy/local cache
const CACHE_TTL_MS = 5 * 60 * 1000;              // 5 mins cache for check-access result
const CACHE_KEY = "tt_access_cache_v1";

/**
 * Async check that prefers server verification (/api/check-access).
 * Falls back to localStorage (your old logic) if the API is unreachable.
 */
export async function isSubscribed() {
  // 1) Fast path: short-lived cached verdict to avoid spamming API
  try {
    const cachedRaw = sessionStorage.getItem(CACHE_KEY);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      if (Date.now() - cached.t < CACHE_TTL_MS) return !!cached.ok;
    }
  } catch { /* ignore */ }

  // 2) Ask server (cookie-based, true source of truth)
  try {
    const r = await fetch("/api/check-access", { credentials: "include" });
    const d = await r.json();
    const ok = !!d?.ok;

    // cache brief verdict
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ok, t: Date.now() }));
    } catch {}

    // If ok, optionally sync a friendly local cache (not trusted for gating)
    if (ok) {
      try {
        const payload = {
          active: true,
          planId: d.plan || "UNKNOWN",
          purchasedAt: Date.now(),
          expiresAt: d.expiresAt || null,
          source: "server",
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {}
    }

    return ok;
  } catch {
    // 3) Fallback to your previous localStorage logic (offline / dev)
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (data?.expiresAt && Date.now() > data.expiresAt) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      return !!data?.active;
    } catch {
      return false;
    }
  }
}

/** Legacy: keep for offline testing (no longer used for real unlock). */
export function saveSubscription({ planId, paymentId, email, tenureDays = 365 }) {
  const now = Date.now();
  const expiresAt = now + tenureDays * 24 * 60 * 60 * 1000;
  const payload = {
    active: true,
    planId,
    paymentId,
    email,
    purchasedAt: now,
    expiresAt,
    source: "local",
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

  // also clear API verdict cache so UI re-checks on next view
  try { sessionStorage.removeItem(CACHE_KEY); } catch {}
}

export function clearSubscription() {
  localStorage.removeItem(STORAGE_KEY);
  try { sessionStorage.removeItem(CACHE_KEY); } catch {}
}

export function getSubscription() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
